import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Reveal, Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import { company } from "../mock";

const topics = ["Book an Assessment", "For Schools", "Start a Business / Partner", "General Enquiry"];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill the required fields", description: "Name, email and message are required." });
      return;
    }
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast({ title: "Message sent", description: "Thank you. Our team will reach out shortly." });
      setForm({ name: "", email: "", phone: "", topic: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again, or call us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      <Seo
        title="Contact MapMyKid | Book a Child Cognitive Assessment"
        description="Get in touch with MapMyKid to book a cognitive assessment for your child, bring assessments to your school, or explore our partner business program. Send us a message and we usually reply within one working day."
      />
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-14">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-up">
          <Eyebrow>Contact Us</Eyebrow>
          <p className="mt-6 text-[18px] sm:text-[20px] leading-relaxed text-slate-600 max-w-2xl mx-auto">
            Book an assessment, bring MapMyKid to your school, or explore a partnership. We usually respond within one working day.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-3xl border border-violet-100 bg-white p-7 sm:p-9 shadow-xl shadow-violet-100/50">
              {done ? (
                <div className="py-10 text-center">
                  <CheckCircle2 className="mx-auto h-16 w-16 text-teal-500" />
                  <h3 className="mt-4 text-[24px] font-bold text-slate-900">Thank you!</h3>
                  <p className="mt-2 text-[16px] text-slate-600">Your message has been received. Our team will get in touch shortly.</p>
                  <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-full h-12 px-6 text-[16px] border-violet-200 text-violet-700 hover:bg-violet-50">Send another message</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="flex items-center gap-2 text-violet-700"><MessageSquare className="h-5 w-5" /><span className="text-[16px] font-semibold">Send us a message</span></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-[16px]">Full name *</Label>
                      <Input value={form.name} onChange={update("name")} placeholder="Your name" className="h-12 text-[16px] rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[16px]">Phone</Label>
                      <Input value={form.phone} onChange={update("phone")} placeholder="Your phone number" className="h-12 text-[16px] rounded-xl" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[16px]">Email *</Label>
                    <Input type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="h-12 text-[16px] rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[16px]">I am interested in</Label>
                    <Select value={form.topic} onValueChange={update("topic")}>
                      <SelectTrigger className="h-12 text-[16px] rounded-xl"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                      <SelectContent>
                        {topics.map((t) => <SelectItem key={t} value={t} className="text-[16px]">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[16px]">Message *</Label>
                    <Textarea value={form.message} onChange={update("message")} placeholder="Tell us about your child, school or business goal..." className="min-h-[130px] text-[16px] rounded-xl" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full cta-gradient text-white rounded-full h-14 text-[17px] font-semibold">
                    {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...</> : <>Send Message <Send className="ml-2 h-5 w-5" /></>}
                  </Button>
                  <p className="text-[15px] text-slate-400 text-center">By submitting, you agree to be contacted by {company.name}.</p>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="mt-10 space-y-5 text-[17px] leading-relaxed text-slate-600">
              <h2 className="text-[24px] sm:text-[28px] font-extrabold text-slate-900">How can we help?</h2>
              <p>
                Whether you are a parent curious about your child's natural abilities, a school looking to understand your learners better, or an entrepreneur exploring a cognitive-tech business, MapMyKid is here to help. Share a few details in the form above and the right member of our team will get back to you, usually within one working day.
              </p>
              <p>
                <span className="font-semibold text-slate-800">Booking an assessment.</span> Tell us your child's grade and we will guide you to the right assessment for their age, from Nursery to Grade 12. Each assessment takes about 60 minutes, is taken online from the comfort of your home, and produces an instant, detailed cognitive profile covering intellect and memory, focus, decision-making speed, creativity and natural ability.
              </p>
              <p>
                <span className="font-semibold text-slate-800">For schools and institutions.</span> If you would like to bring MapMyKid to your school, mention your role and the number of students. We will arrange a demo, walk your leadership and teachers through the platform, and explain how easily assessments can run during a single class period.
              </p>
              <p>
                <span className="font-semibold text-slate-800">Starting your own business.</span> Interested in launching your own MapMyKid brand? Let us know your background and city, and our partner team will share a full deck, pricing and the next steps to get you started with your own cognitive assessment business.
              </p>
              <p>
                Prefer email? You can also reach us any time at <a href={`mailto:${company.email}`} className="font-semibold text-violet-700 hover:underline">{company.email}</a> and we will respond promptly. We read every message personally and aim to point you to the most useful next step, whether that is a quick answer, a call or a demo.
              </p>
              <p>
                MapMyKid is trusted by parents, educators and schools who want clarity that goes beyond marks. If you are unsure where to begin, simply tell us a little about your goal and we will happily guide you through the options that fit your child, classroom or business best.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
